import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "@shared/v2/Tooltip";
import TabBar from "@shared/v2/TabBar";
import Tab from "@shared/v2/Tab";
import PersonInteractionEmailForm from "./PersonInteractionEmailForm";
import SmsConversation from "../../SmsConversation/components/SmsConversation";
import PersonOtherInteractionForm from "./PersonOtherInteractionForm";
import PersonInteractionsNoteForm from "./PersonInteractionsNoteForm";
import TaskForm from "../../Tasks/components/TaskForm";
import * as actions from "../actions/personDetailActionCreators";
import Loading from "../../Goals/components/Loader";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import SingleDialer from "../../Dialer/SingleDialer";

const TABS = ["Note", "Email", "Call", "Text", "Appointment", "Other"];

const getTooltipContent = (text, platform) => {
  if (text === "Email") return "No Email Address";
  if (text === "Call" || text === "Text") {
    if (!platform) {
      return `${text === "Call" ? "The dialer" : "Texting"} is a Brivity Platform feature - call (866) 295-3322 or email sales@brivity.com to upgrade!`;
    }
    return "No Phone Number";
  }
  return text;
};

const TooltipOrText = ({ platform, disabled, text }) => {
  if (disabled) {
    return <Tooltip placement="bottom" trigger={text} content={getTooltipContent(text, platform)} />;
  }
  return text;
};

const PersonInteractions = ({ setOpenAddAgreementModal }) => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();
  const person = useSelector((state) => state.personDetail.person);
  const gmailReplyThreadId = useSelector((state) => state.personDetail.gmailReplyThreadId);
  const gmailReplyThreadSubject = useSelector((state) => state.personDetail.gmailReplyThreadSubject);
  const additionalRecipientsVal = useSelector((state) => state.personDetail.additionalRecipientsVal);
  const CCval = useSelector((state) => state.personDetail.CCval);
  const BCCval = useSelector((state) => state.personDetail.BCCval);
  const sendEmailError = useSelector((state) => state.personDetail.sendEmailError);
  const isEmailSent = useSelector((state) => state.personDetail.isEmailSent);
  const selectedInteractionTab = useSelector((state) => state.personDetail.selectedInteractionTab);
  const selectedMessageUUID = useSelector((state) => state.personDetail.selectedMessageUUID);
  const defaultHomeAppTabContent = useSelector((state) => state.personDetail.defaultHomeAppTabContent);
  const task = useSelector((state) => state.personDetail.task);

  const updatePersonData = (data) => dispatch(actions.updatePersonData(data));
  const touchHomeAppInviteSent = (id) => dispatch(actions.touchHomeAppInviteSent(id));
  const submitNote = (...args) => dispatch(actions.submitNote(...args));
  const submitEmail = (...args) => dispatch(actions.submitEmail(...args));
  const handleSubmitEmailError = (...args) => dispatch(actions.handleSubmitEmailError(...args));
  const addSmsToTimeline = (...args) => dispatch(actions.addSmsToTimeline(...args));
  const submitOtherInteraction = (...args) => dispatch(actions.submitOtherInteraction(...args));

  const detailsObjects = (type) => {
    return person.included?.filter((a) => a.type === type).map((a) => a.attributes) || [];
  };

  const selectInteractionTab = (tabIndex) => {
    if (tabIndex === 1) {
      dispatch(actions.setGmailReplyThread(null, null, [], [], []));
    }
    dispatch(actions.setInteractionTab(tabIndex));
    dispatch(actions.clearInteractionTabContent());
  };

  const setMessageUUID = (messageUUID) => {
    dispatch(actions.setMessageUUID(messageUUID));
  };

  //TODO: if using defaultHomeAppTabContent for setting default tab content from somewhere other than Home App card, then we should rename this prop
  //See comments in PersonInteractionEmailForm and SmsConversation
  const emailTabDisabled = detailsObjects("email_detail").length < 1;
  const callTabDisabled =
    !featureFlags.platformAccount || person.data?.attributes.primary_phone_number.length < 1;
  const smsTabDisabled =
    !featureFlags.platformAccount || person.data?.attributes.primary_phone_number.length < 1;

  useEffect(() => {
    if (!person.data?.id) return;
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get("tab");
    if (tab) {
      let tabNumber = parseInt(tab, 10);
      if (isNaN(tabNumber)) {
        tabNumber = 0;
      }
      selectInteractionTab(tabNumber);
    }
    const messageUUID = urlParams.get("messageUUID");
    if (messageUUID) {
      setMessageUUID(messageUUID);
    }
  }, [person.data?.id]);

  return (
    <div className="tw-shadow-tdp-card tw-relative">
      {!person.data && <Loading />}
      <TabBar isPadded containerClassName="tw-bg-white tw-rounded-t-[4px] tw-w-full">
        {TABS.map((tab, index) => (
          <Tab
            key={tab}
            label={
              <TooltipOrText
                platform={featureFlags.platformAccount}
                disabled={
                  (tab === "Email" && emailTabDisabled) ||
                  (tab === "Call" && callTabDisabled) ||
                  (tab === "Text" && smsTabDisabled)
                }
                text={tab}
              />
            }
            selected={selectedInteractionTab === index}
            disabled={
              (tab === "Email" && emailTabDisabled) ||
              (tab === "Call" && callTabDisabled) ||
              (tab === "Text" && smsTabDisabled)
            }
            onClick={() => selectInteractionTab(index)}
          />
        ))}
      </TabBar>

      <div className="tw-rounded-b-[4px] tw-bg-white tw-p-[20px]">
        {selectedInteractionTab === 0 && (
          <div className="tw-flex tw-flex-col tw-max-h-[400px]">
            <PersonInteractionsNoteForm
              hideCancel
              person={person}
              submitNote={submitNote}
              notableId={person.data?.id}
              notableType="Person"
            />
          </div>
        )}

        {selectedInteractionTab === 1 && (
          <PersonInteractionEmailForm
            person={person}
            updatePersonData={updatePersonData}
            submitEmail={submitEmail}
            handleSubmitEmailError={handleSubmitEmailError}
            send_email_url={person.send_email_url}
            current_user_id={currentUser.id}
            gmailReplyThreadId={gmailReplyThreadId}
            additionalRecipientsVal={additionalRecipientsVal}
            CCval={CCval}
            BCCval={BCCval}
            gmailReplyThreadSubject={gmailReplyThreadSubject}
            sendEmailError={sendEmailError}
            isEmailSent={isEmailSent}
            defaultHomeAppTabContent={defaultHomeAppTabContent}
          />
        )}

        {selectedInteractionTab === 2 && <SingleDialer leadUuid={person?.data?.attributes?.uuid} />}

        {selectedInteractionTab === 3 && (
          <SmsConversation
            person={person}
            addSmsToTimeline={addSmsToTimeline}
            defaultHomeAppTabContent={defaultHomeAppTabContent}
            touchHomeAppInviteSent={touchHomeAppInviteSent}
            selectedMessageUUID={selectedMessageUUID}
          />
        )}

        {selectedInteractionTab === 4 && (
          <div className="tw-flex tw-flex-col tw-gap-[32px]">
            <TaskForm
              person={person}
              onClose={() => window.location.reload()}
              agreementCallBack={setOpenAddAgreementModal}
              isCreatingAppointment
              task={task}
              user={currentUser}
              contactEmail={person.data?.attributes.main_email}
              hideCancel
            />
          </div>
        )}

        {selectedInteractionTab === 5 && (
          <PersonOtherInteractionForm
            person={person}
            current_user_id={currentUser.id}
            submitOtherInteraction={submitOtherInteraction}
          />
        )}
      </div>
    </div>
  );
};

export default PersonInteractions;
